/** The claims the session checks. A JWT carries more; these are the ones used. */
export interface JwtClaims {
  /** Expiry, in seconds since the epoch. */
  exp?: number;
  /** Issued-at, in seconds since the epoch. */
  iat?: number;
  [claim: string]: unknown;
}

/**
 * Decodes a JWT payload, returning null for anything unreadable.
 *
 * Returning null rather than throwing is the point. The previous hand-rolled
 * version ran inside a `new Promise` executor in the route guard, so a missing or
 * malformed token threw where nothing caught it — the promise never settled and
 * navigation hung with no error and no redirect.
 *
 * This does NOT verify the signature. It reads claims the server has already
 * vouched for; the backend remains the only real authority on validity.
 */
export function decodeJwt(token: string | null | undefined): JwtClaims | null {
  if (!token) return null;

  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const claims = JSON.parse(json);
    return claims && typeof claims === 'object' ? claims : null;
  } catch {
    return null;
  }
}
